import { useEffect, useState, type ReactNode } from "react";
import { IconEyeOff } from "@tabler/icons-react";
import {
  Button,
  ConfirmModalFooter,
  Group,
  IconButton,
  Modal,
  Progress,
  Stack,
  Text,
  WP_ICON_NAV,
} from "@wapharma/design-system";
import { useProductBulkImportContextOptional } from "../contexts/ProductBulkImportContext";
import { useToast } from "../contexts/ToastContext";
import type { ImportJobStatus } from "../hooks/useCatalogImportJobInbox";
import type { useProductBulkImport } from "./useProductBulkImport";
import { InboxDrillableHint } from "./InboxDrillableHint";
import { ImportJobStatusDot } from "./ImportJobStatusDot";
import styles from "./InboxBulkImportItems.module.css";

type ImportState = ReturnType<typeof useProductBulkImport>;

function BulkImportProvisioningSummary({
  provisioning,
}: {
  provisioning: NonNullable<ImportState["result"]>["provisioning"];
}) {
  if (!provisioning) return null;
  const {
    markesCreated,
    categoriesCreated,
    productTypesCreated,
    imagesUploaded,
    imageFailures,
  } = provisioning;
  const hasAnything =
    markesCreated.length > 0 ||
    categoriesCreated.length > 0 ||
    (productTypesCreated?.length ?? 0) > 0 ||
    imagesUploaded.length > 0 ||
    imageFailures.length > 0;
  if (!hasAnything) return null;

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600}>
        Détecté pendant l’import
      </Text>
      {markesCreated.length > 0 ? (
        <Text size="xs" c="dimmed">
          Marques créées ({markesCreated.length})
        </Text>
      ) : null}
      {categoriesCreated.length > 0 ? (
        <Text size="xs" c="dimmed">
          Catégories créées ({categoriesCreated.length})
        </Text>
      ) : null}
      {(productTypesCreated?.length ?? 0) > 0 ? (
        <Text size="xs" c="dimmed">
          Types produit créés ({productTypesCreated!.length})
        </Text>
      ) : null}
      {imagesUploaded.length > 0 ? (
        <Text size="xs" c="dimmed">
          Images importées : {imagesUploaded.length}
        </Text>
      ) : null}
      {imageFailures.length > 0 ? (
        <Text size="xs" c="error">
          Images en échec : {imageFailures.length}
        </Text>
      ) : null}
    </Stack>
  );
}

function InboxImportCard({
  title,
  subtitle,
  status,
  unread,
  seenLabel,
  children,
  onDismiss,
  dismissDisabled,
  onOpen,
}: {
  title: string;
  subtitle?: string;
  status: ImportJobStatus;
  unread?: boolean;
  seenLabel?: string | null;
  children?: ReactNode;
  onDismiss?: () => void;
  /** Masquer indisponible (ex. import en cours). */
  dismissDisabled?: boolean;
  onOpen?: () => void;
}) {
  return (
    <article
      className={`${styles.card} ${unread ? styles.cardUnread : styles.cardRead}`}
      data-inbox-drillable={onOpen ? "" : undefined}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      style={onOpen ? { cursor: "pointer" } : undefined}
    >
      <div className={styles.cardHead}>
        <ImportJobStatusDot
          status={status}
          size={12}
          className={styles.cardStatus}
        />
        <div className={styles.cardTitles}>
          <Text size="sm" fw={unread ? 600 : 500} className={styles.cardTitle}>
            {title}
          </Text>
          {subtitle ? (
            <Text size="xs" className={styles.cardSubtitle}>
              {subtitle}
            </Text>
          ) : null}
          {seenLabel ? (
            <Text
              size="xs"
              c="dimmed"
              className={styles.cardSeenLabel}
            >
              {seenLabel}
            </Text>
          ) : null}
        </div>
        <div className={styles.cardActionsInline}>
          {onOpen ? <InboxDrillableHint /> : null}
          {onDismiss || dismissDisabled ? (
            <IconButton
              type="button"
              size="xs"
              variant="ghost"
              borderless
              className={styles.dismiss}
              aria-label={
                dismissDisabled
                  ? "Masquer indisponible pendant l'import"
                  : "Masquer"
              }
              title={
                dismissDisabled
                  ? "Import en cours — masquage indisponible"
                  : undefined
              }
              disabled={dismissDisabled}
              onClick={
                dismissDisabled
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      onDismiss?.();
                    }
              }
            >
              <IconEyeOff {...WP_ICON_NAV} />
            </IconButton>
          ) : null}
        </div>
      </div>
      {children ? <div className={styles.cardBody}>{children}</div> : null}
    </article>
  );
}

/** Catalog bulk-import progress, errors, and results — inbox cards (not create-product menu). */
export function InboxBulkImportItems({
  onOpenJob,
  onDismissJob,
  isJobSeen,
  onMarkJobSeen,
}: {
  onOpenJob?: (jobId: string) => void;
  onDismissJob?: (jobId: string, status: ImportJobStatus) => void;
  isJobSeen?: (jobId: string, status: ImportJobStatus) => boolean;
  onMarkJobSeen?: (jobId: string, status: ImportJobStatus) => void | Promise<void>;
}) {
  const { showToast } = useToast();
  const bulkImport = useProductBulkImportContextOptional();
  const [cancelOpen, setCancelOpen] = useState(false);

  const importState = bulkImport?.importState;
  const trackedImportJobId = bulkImport?.trackedImportJobId ?? null;
  const trackedImportFileName = bulkImport?.trackedImportFileName ?? null;
  const cancelImportJob = bulkImport?.cancelImportJob;
  const cancelImportPending = bulkImport?.cancelImportPending ?? false;

  const importProgress = importState?.importProgress ?? null;
  const jobStatus = importState?.jobStatus ?? null;
  const jobInFlight = importState?.jobInFlight ?? false;
  const activeJobId = importState?.activeJobId ?? null;
  const loading = importState?.loading ?? false;
  const err = importState?.err ?? null;
  const result = importState?.result ?? null;
  const closeImportPopover = importState?.closeImportPopover;

  const showErr = Boolean(
    err && (importProgress || result || loading || jobInFlight),
  );
  const showProgress = Boolean(
    importProgress &&
    (jobInFlight ||
      loading ||
      jobStatus === "PENDING" ||
      jobStatus === "RUNNING"),
  );

  useEffect(() => {
    if (showErr && err) showToast(err, { tone: "error" });
  }, [showErr, err, showToast]);

  if (!bulkImport || !importState || !closeImportPopover || !cancelImportJob) {
    return null;
  }

  if (!showProgress && !result && !showErr) return null;

  const openJobId = trackedImportJobId ?? activeJobId;
  const openDetail =
    openJobId && onOpenJob
      ? () => {
          if (openJobId && jobStatus) {
            void onMarkJobSeen?.(openJobId, jobStatus);
          }
          onOpenJob(openJobId);
        }
      : undefined;

  const progressUnread =
    openJobId && isJobSeen
      ? !isJobSeen(openJobId, jobStatus === "PENDING" || jobStatus === "RUNNING" ? jobStatus : "RUNNING")
      : true;

  const resultStatus: ImportJobStatus =
    result && result.failed > 0 && result.created === 0 ? "FAILED" : "COMPLETED";
  const resultUnread = openJobId && isJobSeen ? !isJobSeen(openJobId, resultStatus) : true;

  const dismissJob = (status: ImportJobStatus) => {
    if (openJobId) {
      void onMarkJobSeen?.(openJobId, status);
      onDismissJob?.(openJobId, status);
    }
    closeImportPopover();
  };

  const progressStatus: ImportJobStatus =
    jobStatus === "PENDING" || jobStatus === "RUNNING" ? jobStatus : "RUNNING";

  const handleCancel = async () => {
    const ok = await cancelImportJob();
    if (ok) setCancelOpen(false);
  };

  return (
    <Stack gap="sm" className={styles.list} aria-label="Import catalogue">
      {showProgress && importProgress ? (
        <InboxImportCard
          title="Import produits en cours"
          status={progressStatus}
          unread={progressUnread}
          subtitle={
            trackedImportFileName ??
            `${importProgress.current.toLocaleString("fr-FR")} / ${importProgress.total.toLocaleString("fr-FR")}`
          }
          onOpen={openDetail}
          onDismiss={() => dismissJob(progressStatus)}
          dismissDisabled
        >
          <Progress
            value={importProgress.current}
            max={importProgress.total || 1}
            tone="neutral"
            size="md"
            className={styles.importProgress}
          />
          <Text size="xs" c="dimmed" className={styles.progressMeta}>
            {importProgress.current.toLocaleString("fr-FR")} /{" "}
            {importProgress.total.toLocaleString("fr-FR")}
          </Text>
          <Group
            gap="xs"
            justify="flex-end"
            wrap="wrap"
            className={styles.cardActions}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="danger"
              size="xs"
              disabled={cancelImportPending}
              onClick={(e) => {
                e.stopPropagation();
                setCancelOpen(true);
              }}
            >
              Annuler l&apos;import
            </Button>
          </Group>
          {loading && activeJobId ? (
            <Text size="xs" c="dimmed" className={styles.jobHint}>
              Job {activeJobId.slice(0, 8)}… — vous pouvez fermer l’aperçu
              catalogue.
            </Text>
          ) : null}
        </InboxImportCard>
      ) : null}

      {showErr ? (
        <InboxImportCard
          title="Import produits — erreur"
          status="FAILED"
          unread={openJobId && isJobSeen ? !isJobSeen(openJobId, "FAILED") : true}
          subtitle={trackedImportFileName ?? undefined}
          onDismiss={() => dismissJob("FAILED")}
        />
      ) : null}

      {result ? (
        <InboxImportCard
          title="Import produits terminé"
          status={resultStatus}
          unread={resultUnread}
          subtitle={trackedImportFileName ?? undefined}
          onDismiss={() => dismissJob(resultStatus)}
          onOpen={openDetail}
        >
          <BulkImportProvisioningSummary provisioning={result.provisioning} />
        </InboxImportCard>
      ) : null}

      <Modal
        opened={cancelOpen}
        onClose={() => !cancelImportPending && setCancelOpen(false)}
        title="Annuler l'import ?"
        footer={
          <ConfirmModalFooter
            cancelLabel="Continuer"
            confirmLabel={cancelImportPending ? "Annulation…" : "Annuler l'import"}
            danger
            onCancel={() => !cancelImportPending && setCancelOpen(false)}
            onConfirm={() => !cancelImportPending && void handleCancel()}
          />
        }
      >
        <Text size="sm">
          L&apos;import s&apos;arrêtera après le lot de lignes en cours (~250
          lignes max). Les produits déjà créés restent en base.
        </Text>
      </Modal>
    </Stack>
  );
}
